import { useCallback, useEffect, useState } from 'react';
import { getActionsForConfig } from './onboarding.catalog';
import { Objective, OWNER_OBJECTIVES, Profile } from './onboarding.types';
import { OnboardingStateDTO, onboardingApi } from './onboarding.api';

// Re-export so callers of the hook don't need to know about the API layer
export { fireOnboardingEvent } from './onboarding.api';

// ---------------------------------------------------------------------------
// Helpers (pure — no side effects)
// ---------------------------------------------------------------------------

export function computeProgress(state: OnboardingStateDTO): number {
  const actions = getActionsForConfig(state.profile, state.objective);
  if (actions.length === 0) return 0;
  const done = actions.filter((a) => state.completions[a.id] === 'done').length;
  return Math.round((done / actions.length) * 100);
}

/**
 * Post-login redirect route.
 * Always returns '/onboarding' because:
 * 1. New users MUST see the onboarding page
 * 2. Users who already completed onboarding are redirected to /dashboard
 *    by the OnboardingPage component itself (after loading backend state)
 * 
 * The backend is the sole authority on onboarding state.
 */
export function getPostLoginRoute(accountType?: string): string {
  // Les comptes pro (agences / pros) n'ont pas de parcours onboarding
  // propriétaire/recherche : ils vont directement au tableau de bord.
  if (accountType === 'pro') return '/dashboard';
  return '/onboarding';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOnboarding() {
  // Initialize with null — we load from backend only, not from local cache
  const [state, setState] = useState<OnboardingStateDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // On mount, fetch from backend to get the authoritative state
  useEffect(() => {
    setLoading(true);
    setError(null);
    onboardingApi
      .getState()
      .then((data) => {
        setState(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  const refreshState = useCallback(() => {
    setLoading(true);
    return onboardingApi
      .getState(true)
      .then((data) => {
        setState(data);
        setLoading(false);
        return data;
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
        throw err;
      });
  }, []);

  const setProfile = useCallback((profile: Profile) => {
    const defaultObjective: Objective = profile === 'owner' ? 'sell' : 'active_buy';
    return onboardingApi.updateProfile(profile, defaultObjective).then(() => {
      setState((prev) =>
        prev
          ? { ...prev, profile, objective: defaultObjective }
          : {
              profile,
              objective: defaultObjective,
              completions: {},
              configuredAt: null,
            }
      );
    });
  }, []);

  const setObjective = useCallback((objective: Objective) => {
    return onboardingApi.updateObjective(objective).then(() => {
      setState((prev) => (prev ? { ...prev, objective } : null));
    });
  }, []);

  // Fallback to defaults if still loading
  const currentState = state || {
    profile: 'owner' as Profile,
    objective: 'sell' as Objective,
    completions: {},
    configuredAt: null,
  };

  const actions = getActionsForConfig(currentState.profile, currentState.objective);
  const progress = computeProgress(currentState);
  const remainingActions = actions.filter((a) => currentState.completions[a.id] !== 'done');
  const completedActions = actions.filter((a) => currentState.completions[a.id] === 'done');
  const isOwner = currentState.profile === 'owner';
  const availableObjectives = isOwner ? OWNER_OBJECTIVES : ['active_buy', 'active_rent', 'passive'];

  const explainerTransactionOwnerDone = currentState.explainerTransactionOwnerDone === true;
  const explainerTransactionSearcherDone = currentState.explainerTransactionSearcherDone === true;

  return {
    state: currentState,
    loading,
    error,
    actions,
    progress,
    remainingActions,
    completedActions,
    availableObjectives,
    explainerTransactionOwnerDone,
    explainerTransactionSearcherDone,
    setProfile,
    setObjective,
    refreshState,
  };
}